import { useState } from 'react';
import {
  createAdmin,
  getAdminIdentity,
  getAdmins,
  patchAdmin,
  type AdminAccount,
  type AdminRole,
} from '../api';
import { badgeClass, fmtDate } from '../format';
import { useApi } from '../useApi';

const ASSIGNABLE: AdminRole[] = ['admin', 'moderator', 'support'];

const initial = (s: string) => (s.trim().charAt(0) || '?').toUpperCase();

/** Owner-only: staff accounts — create, change role, deactivate, reset password. */
export function Admins() {
  const { data, error, loading, reload } = useApi(getAdmins);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const me = getAdminIdentity();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AdminRole>('moderator');
  const [creating, setCreating] = useState(false);

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed.');
    } finally {
      setBusyId(null);
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setActionError(null);
    try {
      await createAdmin({ email: email.trim(), name: name.trim(), password, role });
      setEmail('');
      setName('');
      setPassword('');
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Create failed.');
    } finally {
      setCreating(false);
    }
  };

  const resetPassword = (a: AdminAccount) => {
    const next = window.prompt(`New password for ${a.email} (min 8 chars):`);
    if (!next) return;
    if (next.length < 8) {
      setActionError('Password must be at least 8 characters.');
      return;
    }
    void run(a.id, () => patchAdmin(a.id, { password: next }));
  };

  const rows: AdminAccount[] = data?.data ?? [];

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Admins<span className="dot">.</span>
        </h1>
        {data && <span className="muted">{rows.length} accounts</span>}
      </div>
      <p className="hint" style={{ marginBottom: 16 }}>
        Staff accounts (owner only). The single owner comes from first-run setup; created accounts top out at “admin”.
        Deactivate instead of deleting so the audit log keeps its author.
      </p>
      {actionError && <p className="error-line">{actionError}</p>}

      <form className="card" onSubmit={create}>
        <div className="card-head">
          <span className="card-title">New admin</span>
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Email</label>
            <input
              className="input"
              type="email"
              placeholder="email@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Name</label>
            <input
              className="input"
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Password</label>
            <input
              className="input"
              type="password"
              placeholder="Password (min 8)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Role</label>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value as AdminRole)}>
              {ASSIGNABLE.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={creating || !email.trim() || !name.trim() || password.length < 8}
          >
            {creating ? 'Creating…' : 'Create admin'}
          </button>
        </div>
      </form>

      <div className="table-wrap">
        {error ? (
          <div className="empty">{error}</div>
        ) : loading && !data ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No admin accounts.</div>
        ) : (
          <table className="flm">
            <thead>
              <tr>
                <th>Admin</th>
                <th>Role</th>
                <th>2FA</th>
                <th>Status</th>
                <th>Last login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const isOwner = a.role === 'owner';
                const isSelf = me?.id === a.id;
                return (
                  <tr key={a.id}>
                    <td>
                      <div className="user-cell">
                        <span className="avatar">{initial(a.name || a.email)}</span>
                        <div>
                          <div className="uc-name">
                            {a.name}
                            {isSelf && <span className="muted"> (you)</span>}
                          </div>
                          <div className="uc-sub">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {isOwner ? (
                        <span className="badge badge-warn">owner</span>
                      ) : (
                        <select
                          className="input cell"
                          value={a.role}
                          disabled={busyId === a.id}
                          onChange={(e) =>
                            void run(a.id, () => patchAdmin(a.id, { role: e.target.value as AdminRole }))
                          }
                        >
                          {ASSIGNABLE.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {a.totpEnrolled ? <span className="badge badge-ok">on</span> : <span className="muted">—</span>}
                    </td>
                    <td>
                      <span className={badgeClass(a.isActive ? 'active' : 'suspended')}>
                        {a.isActive ? 'active' : 'inactive'}
                      </span>
                    </td>
                    <td className="muted">{fmtDate(a.lastLoginAt)}</td>
                    <td className="muted">{fmtDate(a.createdAt)}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn" disabled={busyId === a.id} onClick={() => resetPassword(a)}>
                          Reset password
                        </button>
                        {!isOwner &&
                          (a.isActive ? (
                            <button
                              className="btn btn-danger"
                              disabled={busyId === a.id || isSelf}
                              title={isSelf ? 'You cannot deactivate yourself' : undefined}
                              onClick={() => void run(a.id, () => patchAdmin(a.id, { isActive: false }))}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn"
                              disabled={busyId === a.id}
                              onClick={() => void run(a.id, () => patchAdmin(a.id, { isActive: true }))}
                            >
                              Reactivate
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
